import { effect, inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { filter, Observable } from 'rxjs';
import { TabData, DialogActions, DialogData, DialogMode, DialogVariant } from './types';
import { Dialog } from './dialog';
import { NavigationStart, Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class DialogService <T> {
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private activeDialogRef: MatDialogRef<Dialog, T | undefined> | null = null;

  openTabbedDialog(
    tabs: TabData[],
    config?: MatDialogConfig & {
      title?: string;
      defaultActions?: DialogActions;
      mode?: DialogMode;
      variant?: DialogVariant;
      hideSingleTabHeader?: boolean;
      width?: string;
      headerIcon?: string;
      headerIconClass?: string;
      headerDate?: string;
    }
  ): Observable<T | undefined> {
    const mode = config?.mode ?? 'bounded';
    const variant = config?.variant;
    const title = config?.title || (tabs.length === 1 ? tabs[0].name : undefined);
    const modeDefaults = this.getModeDefaults(mode, variant);
    const panelClass = this.buildPanelClasses(mode, variant, config?.panelClass);

    const dialogConfig: MatDialogConfig = {
      ...modeDefaults,
      disableClose: true,
      autoFocus: config?.autoFocus ?? 'dialog',
      ...config,
      width: config?.width ?? modeDefaults.width,
      height: config?.height ?? modeDefaults.height,
      maxWidth: config?.maxWidth ?? modeDefaults.maxWidth,
      maxHeight: config?.maxHeight ?? modeDefaults.maxHeight,
      panelClass,
      data: {
        tabs,
        title,
        defaultActions: config?.defaultActions,
        mode,
        variant,
        hideSingleTabHeader: config?.hideSingleTabHeader,
        headerIcon: config?.headerIcon,
        headerIconClass: config?.headerIconClass,
        headerDate: config?.headerDate
      }
    };

    this.activeDialogRef = this.dialog.open<Dialog, DialogData, T>(Dialog, dialogConfig);
    return this.activeDialogRef.afterClosed();
  }

  private getModeDefaults(mode: DialogMode, variant?: DialogVariant): Partial<MatDialogConfig> {
    if (mode === 'bounded' && variant === 'compact') {
      return { maxWidth: '95vw' };
    }

    const defaults: Record<DialogMode, Partial<MatDialogConfig>> = {
      bounded: { width: '90vw', maxWidth: '90vw' },
      intrinsic: { maxWidth: '95vw' },
      media: { width: 'min(95vw, 55rem)', height: 'min(95vh, 55rem)', maxWidth: '95vw' },
      table: { width: 'min(95vw, 120rem)', height: 'min(90vh, 50rem)', maxWidth: '95vw' },
    };

    return defaults[mode];
  }

  private buildPanelClasses(
    mode: DialogMode,
    variant: DialogVariant | undefined,
    configured: MatDialogConfig['panelClass']
  ): string[] {
    const defaults = [
      'dialog-panel',
      `dialog-panel--${mode}`,
      ...(variant ? [`dialog-panel--${variant}`] : []),
    ];

    return this.mergePanelClasses(configured, defaults);
  }

  private mergePanelClasses(
    configured: MatDialogConfig['panelClass'],
    defaults: string[]
  ): string[] {
    const normalized = Array.isArray(configured)
      ? configured
      : configured
        ? [configured]
        : [];

    return [...new Set([...defaults, ...normalized])];
  }

  closeDialog(result?: T): void {
    if (this.activeDialogRef) {
      this.activeDialogRef.close(result);
      this.activeDialogRef = null;
    }
  }

  isDialogOpen = () => this.activeDialogRef !== null;

  private onNavigation = effect(() => this.router.events
    .pipe(filter(event => event instanceof NavigationStart))
    .subscribe(() => this.dialog.closeAll())
  );
}
